// React hooks for reading from Supabase with realtime refresh.
// All hooks return { data, error, loading } so callers can handle the
// common three states uniformly.

import { useEffect, useRef, useState } from "react";
import { supabase, isAuthConfigured } from "./supabase";
import type { Database } from "@/types/db";

type TableName = keyof Database["public"]["Tables"];

type TableRow<T extends TableName> = Database["public"]["Tables"][T]["Row"];

type QueryResult<T> = {
  data: T | null;
  error: string | null;
  loading: boolean;
  refresh: () => void;
};

/**
 * Run a Supabase query once on mount, then re-run on `deps` change. The
 * query function is responsible for the actual `.from()` / `.select()` /
 * `.eq()` chain. We don't memoize the query for you — keep it stable.
 */
export function useSupabaseQuery<T>(
  query: () => PromiseLike<{ data: T | null; error: { message: string } | null }>,
  deps: ReadonlyArray<unknown> = [],
): QueryResult<T> {
  const [data,    setData]    = useState<T | null>(null);
  const [error,   setError]   = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [tick,    setTick]    = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.resolve()
      .then(() => query())
      .then(({ data, error }) => {
        if (cancelled) return;
        setData(data);
        if (error) setError(error.message);
        setLoading(false);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
        setLoading(false);
      });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tick, ...deps]);

  return {
    data, error, loading,
    refresh: () => setTick(t => t + 1),
  };
}

/**
 * Subscribe to realtime changes on a table. The `onChange` callback is
 * called with `payload.new` (or undefined for DELETE) and the event type.
 * The caller usually re-fetches its own data when this fires.
 */
export function useRealtimeTable<T extends TableName>(
  table: T,
  filter: string | null,
  onChange: (event: "INSERT" | "UPDATE" | "DELETE", row: TableRow<T> | null) => void,
): void {
  // Stash the latest onChange in a ref so the effect doesn't
  // re-subscribe on every render. Callers usually pass a fresh
  // closure each render (e.g. `() => query.refresh()`); including
  // it in the deps would call supabase.removeChannel and
  // supabase.channel() on every parent re-render, which races
  // with the previous channel's join and trips
  // "cannot add `postgres_changes` callbacks after `subscribe()`".
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    if (!isAuthConfigured()) return;
    const ch = supabase
      .channel(`rt:${table}:${filter ?? "all"}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table, filter: filter ?? undefined },
        (payload) => {
          onChangeRef.current(
            payload.eventType as "INSERT" | "UPDATE" | "DELETE",
            (payload.new as TableRow<T>) ?? null,
          );
        },
      )
      .subscribe();
    return () => { void supabase.removeChannel(ch); };
  }, [table, filter]);
}
